import React, { useEffect, useState } from "react";
import { Colors } from './form_builder.js';
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";

/**
 * Recursively renders a condition expression tree editor.
 * Supports AND/OR nesting and leaf condition editing.
 *
 * @param {object|null} condition - Current condition expression tree node
 * @param {function} onChange - Callback(updatedCondition)
 * @param {function} loadAvailableQuestions - Function to load available questions when needed
 * @param {string} parentType - (internal use) to enforce AND/OR type
 */
export const Condition_Editor = ({ 
  condition, 
  onChange, 
  loadAvailableQuestions = null,
  parentType = null,
  colors = Colors,
  parentEditContext = null
}) => {
  const isGroup = condition && (condition.type === "AND" || condition.type === "OR");
  const { t } = useTranslation();
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);

  // Reset questions loaded state when loadAvailableQuestions function changes
  useEffect(() => {
    if (loadAvailableQuestions) {
      setQuestionsLoaded(false);
      setAvailableQuestions([]);
    }
  }, [loadAvailableQuestions]);

  // Labels are now managed centrally in menu.js via form_builder_labels.js
  // No need to set up individual labels here as they are loaded automatically

  // Function to load questions when needed
  const loadQuestions = async () => {
    if (questionsLoaded || !loadAvailableQuestions) return;
    
    try {
      const questions = await loadAvailableQuestions();
      setAvailableQuestions(questions || []);
      setQuestionsLoaded(true);
    } catch (error) {
      console.error("Error loading available questions:", error);
      setAvailableQuestions([]);
      setQuestionsLoaded(true);
    }
  };

  const handleTypeChange = (e) => {
    onChange({ type: e.target.value, children: [] });
  };

  const handleAddGroup = async (groupType) => {
    // Load questions when creating a new group that might contain conditions
    await loadQuestions();
    const newGroup = { type: groupType, children: [] };
    onChange({ ...condition, children: [...condition.children, newGroup] });
  };

  const handleAddCondition = async () => {
    // Load questions when creating a new condition
    await loadQuestions();
    const newCond = {
      type: "CONDITION",
      questionId: "",
      answerId: "",
      operator: "EQUALS"
    };
    onChange({ ...condition, children: [...condition.children, newCond] });
  };

  const handleDelete = (index) => {
    // Create a copy of the children array and remove the item at specified index
    const updated = [...condition.children];
    updated.splice(index, 1);
    onChange({ ...condition, children: updated });
  };
  
  const handleRemoveAll = () => {
    // Remove the entire condition by setting it to null
    onChange(null);
  };

  const handleChildChange = (index, newChild) => {
    // If newChild is null, remove this child entirely
    if (newChild === null) {
      const updated = [...condition.children];
      updated.splice(index, 1);
      onChange({ ...condition, children: updated });
      return;
    }
    
    // Otherwise update the child with new values
    const updated = [...condition.children];
    updated[index] = newChild;
    onChange({ ...condition, children: updated });
  };

  const handleLeafChange = async (field, value) => {
    // Load questions when user starts interacting with a condition
    if (!questionsLoaded) {
      await loadQuestions();
    }
    onChange({ ...condition, [field]: value });
  };

  // Get contextual colors based on parent context
  let contextColor = colors.editContextQuestion; // Default to question
  if (parentEditContext === 'section') {
    contextColor = colors.editContextSection;
  } else if (parentEditContext === 'answer') {
    contextColor = colors.editContextAnswer;
  }

  const selectedQuestion = availableQuestions.find(q => q.id === condition?.questionId);
  const answerOptions = selectedQuestion?.answers || [];

  // Auto-load questions when this is a CONDITION type
  useEffect(() => {
    if (condition?.type === "CONDITION" && !questionsLoaded && loadAvailableQuestions) {
      loadQuestions();
    }
  }, [condition?.type, questionsLoaded, loadAvailableQuestions]);

  if (!condition) {
    return (
      <div className="card bg-light border-0 p-3 text-center w-100">
        <div className="mb-2 text-muted">
          <small>{t('createCondition', { ns: 'labels' })}</small>
        </div>
        <div className="d-flex justify-content-center">
          <select 
            className="form-select form-select-sm w-100" 
            style={{ maxWidth: "300px" }}
            onChange={handleTypeChange} 
            defaultValue=""
          >
            <option value="">{t('selectConditionType', { ns: 'labels' })}</option>
            <option value="AND">{t('andGroup', { ns: 'labels' })}</option>
            <option value="OR">{t('orGroup', { ns: 'labels' })}</option>
          </select>
        </div>
      </div>
    );
  }

  if (condition.type === "CONDITION") {
    return (
      <div className="card shadow-sm mb-2 w-100" style={{ borderColor: colors.secondaryLight, backgroundColor: colors.white }}>
        <div className="card-body p-2">
          <div className="row g-2 align-items-end w-100 mx-0">
            <div className="col">
              <label className="form-label small mb-1">{t('question', { ns: 'labels' })}</label>
              {!questionsLoaded ? (
                <div className="form-select form-select-sm d-flex align-items-center">
                  <small className="text-muted">{t('loadingQuestions', { ns: 'labels' })}</small>
                </div>
              ) : (
                <select
                  className="form-select form-select-sm"
                  value={condition.questionId}
                  onChange={(e) => handleLeafChange("questionId", e.target.value)}
                >
                  <option value="">{t('selectQuestion', { ns: 'labels' })}</option>
                  {availableQuestions.map((q, i) => (
                    <option key={i} value={q.id}>{q.text || q.code}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="col">
              <label className="form-label small mb-1">{t('operator', { ns: 'labels' })}</label>
              <select
                className="form-select form-select-sm"
                value={condition.operator}
                onChange={(e) => handleLeafChange("operator", e.target.value)}
              >
                <option value="EQUALS">{t('isEqualTo', { ns: 'labels' })}</option>
                <option value="NOT_EQUALS">{t('isNotEqualTo', { ns: 'labels' })}</option>
              </select>
            </div>
            <div className="col">
              <label className="form-label small mb-1">{t('answer', { ns: 'labels' })}</label>
              <select
                className="form-select form-select-sm"
                value={condition.answerId}
                onChange={(e) => handleLeafChange("answerId", e.target.value)}
              >
                <option value="">{t('selectAnswer', { ns: 'labels' })}</option>
                {answerOptions.map((a, i) => (
                  <option key={i} value={a.id}>{a.text || a.code}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <button 
                className="btn btn-outline-danger btn-sm"
                onClick={handleRemoveAll}
                title={t('removeCondition', { ns: 'labels' })}
                style={{height: "31px", width: "40px"}}
              >
                <i className="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-3 shadow-sm border border-light w-100" style={{ borderColor: colors.secondaryLight }}>
      <div className="card-header d-flex align-items-center justify-content-between py-2"
        style={{ backgroundColor: colors.secondaryLight }}>
        <div className="d-flex align-items-center">
          <i className="fa fa-layer-group me-2" style={{ color: contextColor.header }}></i>
          <span className="fw-bold">{condition.type} Group</span>
          <span className="badge ms-2" style={{ backgroundColor: contextColor.header, color: colors.white }}>
            {condition.type === "AND" ? t('allMustBeTrue', { ns: 'labels' }) : t('anyCanBeTrue', { ns: 'labels' })}
          </span>
        </div>
        <div>
          <button className="btn btn-sm me-1" 
            style={{ 
              backgroundColor: contextColor.header, 
              color: colors.white 
            }}
            onClick={handleAddCondition}>
            <i className="fa fa-equals me-1"></i> {t('addCondition', { ns: 'labels' })}
          </button>
          <button className="btn btn-sm me-1" 
            style={{ 
              color: colors.secondary, 
              borderColor: colors.secondary, 
              backgroundColor: colors.white 
            }}
            onClick={() => handleAddGroup("AND")}>
            <i className="fa fa-layer-group me-1"></i> {t('addAnd', { ns: 'labels' })}
          </button>
          <button className="btn btn-sm me-1" 
            style={{ 
              color: colors.secondary, 
              borderColor: colors.secondary, 
              backgroundColor: colors.white 
            }}
            onClick={() => handleAddGroup("OR")}>
            <i className="fa fa-layer-group me-1"></i> {t('addOr', { ns: 'labels' })}
          </button>
          <button className="btn btn-sm" 
            style={{ 
              color: colors.danger, 
              borderColor: colors.danger, 
              backgroundColor: colors.white 
            }}
            onClick={handleRemoveAll}
            title={t('removeEntireCondition', { ns: 'labels' })}
          >
            <i className="fa fa-trash"></i>
          </button>
        </div>
      </div>
      <div className="card-body p-3">
        {condition.children.length === 0 && (
          <div className="text-center py-2 text-muted w-100">
            <small>{t('addConditionsOrGroups', { ns: 'labels' })}</small>
          </div>
        )}
        {condition.children.map((child, index) => (
          <div key={index} className="position-relative mb-2">
            <div className="position-absolute end-0 top-0 mt-1 me-1 z-10">
              <button 
                className="btn btn-sm" 
                style={{ 
                  color: colors.danger, 
                  borderColor: colors.danger, 
                  backgroundColor: colors.white 
                }}
                onClick={() => handleDelete(index)}
                title={t('removeCondition', { ns: 'labels' })}
              >
                <i className="fa fa-trash"></i>
              </button>
            </div>
            <Condition_Editor
              condition={child}
              onChange={(newChild) => handleChildChange(index, newChild)}
              loadAvailableQuestions={loadAvailableQuestions}
              parentType={condition.type}
              colors={colors}
              parentEditContext={parentEditContext}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
